import { clsx, ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import forge from "node-forge";
import { DISPLAYED_PRICE_COMPONENT_KEYS, type DisplayedPriceComponent } from "./consts";


export const sorted = <T>(arr: T[], callback: (a: T, b: T) => number): T[] => {
    try {
        const copy = JSON.parse(JSON.stringify(arr));
        copy.sort(callback);
        return copy;
    } catch (error) {
        return arr;
    }
};

// Every caller gets the result of the run that finally fires (or its error): a call superseded by a later
// one must not hang forever, or whatever awaits it (e.g. the shipping submit lock) never continues.
export const debounce = <T extends (...args: any[]) => Promise<any>>(func: T, delay: number) => { //In milliseconds
  let timer: NodeJS.Timeout;
  let waiters: { resolve: (value: Awaited<ReturnType<T>>) => void; reject: (error: unknown) => void }[] = [];
  return (...args: Parameters<T>) => {
    return new Promise<Awaited<ReturnType<T>>>((resolve, reject) => {
      waiters.push({ resolve, reject });
      clearTimeout(timer);
      timer = setTimeout(async () => {
        const settled = waiters;
        waiters = [];
        try {
          const result = await func(...args);
          settled.forEach((waiter) => waiter.resolve(result));
        } catch (error) {
          settled.forEach((waiter) => waiter.reject(error));
        }
      }, delay);
    });
  };
};



export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const DEVIATION = 0.01;

export const isZeroOrderPrice = (amount: number): boolean => Math.abs(amount) < DEVIATION;

/**
 * Convert major-unit money to integer minor units (cents).
 * Prefer over Math.floor(amount * 100 * rate) — IEEE float floor under-charges by 1¢
 * (e.g. 81.85*100 → 8184.999… → floor 8184). Keep in sync with funnel-common helpers.toMinorUnits.
 */
export const toMinorUnits = (amount: number, currencyRate = 1): number => {
  return Math.round(Number(amount) * Number(currencyRate) * 100);
};

/**
 * HL-12264: whether tracked prices are reported in the order's charge currency — UK only (GBP);
 * every other country reports EUR. `country` is a country id, URL segment or locale ("GB", "uk", "en-GB").
 * Keep in sync with funnel-common helpers.isChargeCurrencyCountry.
 */
export const isChargeCurrencyCountry = (country: string | null | undefined): boolean => {
  const countryID = String(country ?? "").split("-").pop()?.toUpperCase();
  return countryID === "GB" || countryID === "UK";
};

/**
 * HL-12264: a stored (EUR) price in the order's charge currency for UK tracking (GBP), cash-rounded.
 * UK only (isChargeCurrencyCountry); any other country, a rate of 1 or 0/missing, and a missing
 * amount pass through untouched (EUR, as before).
 * Keep in sync with funnel-common helpers.toChargeCurrency.
 */
export const toChargeCurrency = <T extends number | string | null | undefined>(
  amount: T,
  currencyRate: number | string | null | undefined,
  country: string | null | undefined,
): T | number => {
  const rate = Number(currencyRate);
  if (amount == null || !isChargeCurrencyCountry(country) || !rate || rate === 1) return amount;
  return toMinorUnits(Number(amount), rate) / 100;
};

export function toLocaleDateString(date: string | Date, locale?: string, options?: Record<string, string>): string {
  if(!date) {
    return "";
  }
  return new Date(date).toLocaleDateString(locale, options);
}

export function daysDiff(dateOne: Date, dateTwo: Date) {
  const Difference_In_Time = dateOne.getTime() - dateTwo.getTime();
  return Math.abs(Math.round(Difference_In_Time / (1000 * 3600 * 24)));
}

// Nice ease-in-out (slow start & end, fast middle)
export const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// Very slow at beginning and end (quintic curve)
export const easeInOutQuintic = (t: number) =>
  t < 0.5 ? 16 * t * t * t * t * t : 1 - Math.pow(-2 * t + 2, 5) / 2;

// Extremely slow at beginning and end (septic curve)
export const easeInOutSeptic = (t: number) =>
  t < 0.5 ? 64 * t * t * t * t * t * t * t : 1 - Math.pow(-2 * t + 2, 7) / 2;

// Gaussian-like curve with gradual end (fast start, very gradual end)
export const easeOutGaussian = (t: number) => 
  1 - Math.exp(-8 * t);

// Exponential ease-out (fast start, gradual end)
export const easeOutExpo = (t: number) => 
  t === 1 ? 1 : 1 - Math.pow(2, -10 * t);

// Custom asymmetric curve (faster start, more gradual end than beginning)
export const easeOutAsymmetric = (t: number) => 
  1 - Math.pow(1 - t, 3.5);

// Sine-based ease out with extended tail
export const easeOutSineExtended = (t: number) => 
  Math.sin((t * Math.PI) / 2) * (2 - t);

// Custom smooth scroll function for better control
export const smoothScrollTo = (
  el: HTMLElement,
  toTop: number,
  duration = 600,
  easing = (t: number) => t, // default linear; replace below
  forceScroll = true
) => {
  const startTop = el.scrollTop;
  const delta = toTop - startTop;
  const start = performance.now();

  // Function to prevent user scrolling during animation
  const preventScroll = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Function to prevent scroll event
  const preventScrollEvent = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Function to handle keydown events
  const handleKeydown = (e: KeyboardEvent) => {
    // Prevent arrow keys, page up/down, home/end, space from scrolling
    if ([32, 33, 34, 35, 36, 37, 38, 39, 40].includes(e.keyCode)) {
      preventScroll(e);
    }
  };

  // Add scroll prevention if forceScroll is true
  if (forceScroll) {
    el.addEventListener('wheel', preventScroll, { passive: false });
    el.addEventListener('touchmove', preventScroll, { passive: false });
    el.addEventListener('scroll', preventScrollEvent, { passive: false });
    el.addEventListener('keydown', handleKeydown, { passive: false });
  }

  function frame(now: number) {
    const t = Math.min(1, (now - start) / duration); // 0→1
    const eased = easing(t);
    el.scrollTop = startTop + delta * eased;
    if (t < 1) {
      requestAnimationFrame(frame);
    } else {
      // Animation complete - remove scroll prevention
      if (forceScroll) {
        el.removeEventListener('wheel', preventScroll);
        el.removeEventListener('touchmove', preventScroll);
        el.removeEventListener('scroll', preventScrollEvent);
        el.removeEventListener('keydown', handleKeydown);
      }
    }
  }
  requestAnimationFrame(frame);
};

export const normalizeText = (text: string) => text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/**
 * Encrypts a large message using hybrid encryption:
 * - AES-GCM for the payload
 * - RSA-OAEP for the AES key
 */
export function encryptWithPublicKey(publicKeyPem: string, payload: string) {
  // Convert PEM to Forge public key
  const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);

  // Generate a random AES-256 key
  const aesKey = forge.random.getBytesSync(32); // 32 bytes = 256 bits

  //  Generate a random IV (12 bytes recommended for AES-GCM)
  const iv = forge.random.getBytesSync(12);

  // Encrypt the payload using AES-GCM
  const cipher = forge.cipher.createCipher("AES-GCM", aesKey);
  cipher.start({ iv, tagLength: 128 });
  cipher.update(forge.util.createBuffer(payload, "utf8"));
  cipher.finish();

  const encryptedPayload = cipher.output.getBytes();
  const tag = cipher.mode.tag.getBytes(); // Authentication tag

  // Encrypt AES key with RSA-OAEP
  const encryptedKey = publicKey.encrypt(aesKey, "RSA-OAEP");

  // Encode everything in base64 for transport
  return {
    ek: forge.util.encode64(encryptedKey),
    ep: forge.util.encode64(encryptedPayload),
    iv: forge.util.encode64(iv),
    tag: forge.util.encode64(tag),
  };
}

// Client twin of funnel-common displayedPriceComponents (CRA cannot import that helper).
export type { DisplayedPriceComponent };
export type PriceParts = Partial<Record<DisplayedPriceComponent, number | null | undefined>>;


const DISPLAYED_PRICE_KEY_SET = new Set<string>(DISPLAYED_PRICE_COMPONENT_KEYS);

// Accepts catalogs.settings as an object or a JSON string; null if unusable.
const parseCatalogSettingsObject = (settings: unknown): Record<string, unknown> | null => {
  if (!settings) return null;
  if (typeof settings === 'object' && !Array.isArray(settings)) {
    return settings as Record<string, unknown>;
  }
  if (typeof settings === 'string') {
    try {
      const parsed = JSON.parse(settings);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }
  return null;
};

// Reads the opt-in list; null = no override, keep the existing official+service (or FR) formula.
export const parseDisplayedPriceComponents = (settings: unknown): DisplayedPriceComponent[] | null => {
  const parsed = parseCatalogSettingsObject(settings);
  const raw = parsed?.displayed_price_components;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const components = raw.filter((key): key is DisplayedPriceComponent => typeof key === 'string' && DISPLAYED_PRICE_KEY_SET.has(key));
  return components.length > 0 ? components : null;
};

// Displayed medication number: sum only the opted-in parts (missing values count as 0).
export const displayedMedicationPrice = (parts: PriceParts, components: DisplayedPriceComponent[]): number =>
  components.reduce((sum, key) => sum + Number(parts[key] ?? 0), 0);

// True when that fee is baked into the displayed medication number (so hide its standalone row).
export const isPriceComponentIncluded = (
  components: DisplayedPriceComponent[] | null | undefined,
  key: DisplayedPriceComponent,
): boolean => Boolean(components?.includes(key));

/** Client twin of funnel-common PRESCRIBER_COUNTRY_IDS (CRA cannot import that package). */
const PRESCRIBER_COUNTRY_IDS = ["uk", "gb"] as const;

/** HL-10315: public account chat slug — prescriber markets use ask-a-prescriber. */
export const isPrescriberCountry = (country?: string | null): boolean =>
  (PRESCRIBER_COUNTRY_IDS as readonly string[]).includes((country || "").toLowerCase());

/** Last segment of /:country/account/:route — ask-a-prescriber or ask-a-doctor. */
export const getAskADoctorPagePath = (
  country?: string | null,
): 'ask-a-prescriber' | 'ask-a-doctor' =>
  isPrescriberCountry(country) ? 'ask-a-prescriber' : 'ask-a-doctor';

/** Countries that always show the DE-style price breakdown (CartViewDE). */
export const COUNTRIES_WITH_ALWAYS_ON_PRICE_BREAKDOWN = ['DE'] as const;

/**
 * Countries that show the DE-style price breakdown only when the cart/order
 * contains at least one subsidized product (catalogs.is_subsidized).
 */
export const COUNTRIES_WITH_CONDITIONAL_PRICE_BREAKDOWN = ['FR'] as const;

type SubsidizedFlagCarrier = {
  is_subsidized?: boolean | number | null;
};

/**
 * Whether to render the DE-style price breakdown (CartViewDE / CartViewSummaryDE).
 * - DE: always
 * - FR: only if the cart/order has at least one subsidized product, or an explicit isSubsidized flag
 * - CH: never, including for French-language flows
 * - elsewhere: never
 */
export function shouldShowPriceBreakdown(
  country: string | undefined | null,
  products?: SubsidizedFlagCarrier[] | null,
  options?: { isSubsidized?: boolean | number | null }
): boolean {
  const countryUpper = (country || '').toUpperCase();

  if ((COUNTRIES_WITH_ALWAYS_ON_PRICE_BREAKDOWN as readonly string[]).includes(countryUpper)) {
    return true;
  }

  if ((COUNTRIES_WITH_CONDITIONAL_PRICE_BREAKDOWN as readonly string[]).includes(countryUpper)) {
    const fromFlag = options?.isSubsidized != null ? Boolean(Number(options.isSubsidized)) : false;
    const fromProducts = Boolean(products?.some((product) => Boolean(Number(product.is_subsidized))));
    return fromFlag || fromProducts;
  }

  return false;
}
